import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import times from 'lodash/times';
import moment from 'moment';
import {window} from 'ssr-window';
import Arg from '@spothero/arg';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import Config from 'config';
import {initialState as eventInitialState} from 'event/event-reducer';
import {initialState as destinationInitialState} from 'destination/destination-reducer';
import ExperimentUtils from 'utils/experiment';
import SearchTileUtils from './search-tile-utils';

const isBrowser = EnvironmentUtils.isBrowser();
const isMobile = Config.isMobile;
const gmaps = isBrowser ? window.google.maps : null;

const SearchUtils = {
    SEGMENT_SPOT_META: 'sh-segment-spot-meta',

    defaultSearchSettings: {
        searchType: 'address',
        showNoSpotsFound: false,
        currentIncrement: 0,
        zoom: 14,
        isDefaultTimes: true,
        overrideIdealZoom: false,
        destinationTitle: 'Destination',
        sortBy: 'distance',
        isInfoPanelVisible: isBrowser
            ? !StorageUtils.get(Config.infoPanelCookie, 'cookie')
            : false,
        breadcrumbs: null,
        searchMapInstance: null,
        geolocated: false,
        isMobileListViewActive: true,
        mobilePaginationLimit: 30,
        sid: null, // if a spot id is passed as `sid` on page load, this will help facilitate opening up the spot details modal right away
    },

    prepareBreadcrumbs(initData, searchType) {
        const {
            city: {data: city},
            destination: {data: destination},
            event: {data: event},
            searchRequest,
        } = initData;
        const {
            display_name: cityDisplayName,
            latitude: cityLatitude,
            longitude: cityLongitude,
            monthly_relative_url: cityMonthlyUrl,
            relative_url: relativeUrl,
            slug: citySlug,
            street_address: cityStreetAddress,
            state,
            default_image: cityDefaultImage,
            short_description: cityShortDescription,
            monthly_short_description: cityMonthlyShortDescription,
            description: cityDescription,
            monthly_description: cityMonthlyDescription,
        } = city;
        const cityUrl = `city/${relativeUrl}`;
        const monthly = searchRequest ? searchRequest.monthly : false;
        const starts = searchRequest ? searchRequest.starts : null;
        const ends = searchRequest ? searchRequest.ends : null;
        const searchString = searchRequest ? searchRequest.search_string : null;
        const gtDistance = searchRequest ? searchRequest.distance_gt : null;
        const ltDistance = searchRequest ? searchRequest.distance_lt : null;
        const geolocate = searchRequest ? searchRequest.geolocate : false;
        const lowPrice = destination.serp ? destination.serp.low_price : null;
        const highPrice = destination.serp ? destination.serp.high_price : null;
        const hasDestination = !isEmpty(destination);

        return {
            extraBreadcrumb: {
                url:
                    searchType === 'event' || searchType === 'venue'
                        ? `${Config.siteUrl}/category/${citySlug}/theaters-and-venues-parking`
                        : null,
                label:
                    searchType === 'event' || searchType === 'venue'
                        ? 'Venues'
                        : null,
            },
            activeBreadcrumb: {
                url:
                    searchType === 'event'
                        ? `/destination/${citySlug}/${destination.relative_url}`
                        : null,
                label: geolocate
                    ? 'Current Location'
                    : searchType === 'city'
                    ? `${cityDisplayName}, ${state}`
                    : destination.title
                    ? `${destination.title}${
                          searchType === 'event' ? ' Events' : ''
                      }`
                    : searchString
                    ? searchString
                    : 'Downtown',
            },
            city:
                searchType === 'city'
                    ? {
                          latitude: cityLatitude ? cityLatitude : null,
                          longitude: cityLongitude ? cityLongitude : null,
                          url: `${Config.siteUrl}/${
                              monthly && cityMonthlyUrl
                                  ? cityMonthlyUrl
                                  : cityUrl
                          }`,
                          title: `${cityDisplayName}${
                              monthly ? ' Monthly' : ''
                          }`,
                          imgId: cityDefaultImage ? cityDefaultImage.id : null,
                          imgAlt: cityDefaultImage
                              ? cityDefaultImage.alt_text
                              : null,
                          address: !isEmpty(cityStreetAddress)
                              ? cityStreetAddress
                              : null,
                          city: !isEmpty(cityDisplayName)
                              ? cityDisplayName
                              : null,
                          state: !isEmpty(state) ? state : null,
                          lowPrice,
                          highPrice,
                          shortDescription:
                              monthly && !isEmpty(cityMonthlyShortDescription)
                                  ? cityMonthlyShortDescription
                                  : !isEmpty(cityShortDescription)
                                  ? cityShortDescription
                                  : null,
                          description:
                              monthly && !isEmpty(cityMonthlyDescription)
                                  ? cityMonthlyDescription
                                  : !isEmpty(cityDescription)
                                  ? cityDescription
                                  : null,
                      }
                    : {
                          relativeUrl: geolocate ? null : `/${cityUrl}`,
                          displayName: geolocate ? null : cityDisplayName,
                      },
            destination: hasDestination
                ? {
                      id: destination.id,
                      latitude: destination.latitude
                          ? destination.latitude
                          : null,
                      longitude: destination.longitude
                          ? destination.longitude
                          : null,
                      url: `${Config.siteUrl}/${cityUrl}/${
                          monthly && destination.monthly_relative_url
                              ? destination.monthly_relative_url
                              : destination.relative_url
                      }`,
                      title: `${destination.title}${monthly ? ' Monthly' : ''}`,
                      imgId: destination.default_image
                          ? destination.default_image.object.public_id
                          : null,
                      imgAlt: destination.default_image
                          ? destination.default_image.object.alt_text
                          : null,
                      address: !isEmpty(destination.street_address)
                          ? destination.street_address
                          : null,
                      city: !isEmpty(destination.city)
                          ? destination.city
                          : null,
                      state: !isEmpty(destination.state)
                          ? destination.state
                          : null,
                      zip: !isEmpty(destination.zipcode)
                          ? destination.zipcode
                          : null,
                      lowPrice,
                      highPrice,
                      shortDescription:
                          monthly &&
                          !isEmpty(destination.monthly_short_description)
                              ? destination.monthly_short_description
                              : !isEmpty(destination.short_description)
                              ? destination.short_description
                              : null,
                      description:
                          monthly && !isEmpty(destination.monthly_description)
                              ? destination.monthly_description
                              : !isEmpty(destination.description)
                              ? destination.description
                              : null,
                      cityUrl: `${Config.siteUrl}/${cityUrl}`,
                      infoPanelTitle: !isEmpty(
                          destination.destination_info_title
                      )
                          ? destination.destination_info_title
                          : null,
                      monthlyInfoPanelTitle: !isEmpty(
                          destination.monthly_destination_info_title
                      )
                          ? destination.monthly_destination_info_title
                          : null,
                  }
                : destinationInitialState().data,
            event:
                searchType === 'event'
                    ? {
                          id: event.id,
                          title: event.title,
                          metaDescription: !isEmpty(event.meta_description)
                              ? event.meta_description
                              : null,
                          ltDistance,
                          gtDistance,
                          eventStarts: event.starts,
                          eventEnds: event.ends,
                          starts,
                          ends,
                          date: moment(event.starts).format(
                              Config.displayEventDateFormat
                          ),
                          url: `${Config.siteUrl}/${event.search_url}`,
                          shortDescription: !isEmpty(event.short_description)
                              ? event.short_description
                              : null,
                          description: !isEmpty(event.description)
                              ? event.description
                              : null,
                          customDescription: !isEmpty(event.custom_description)
                              ? event.custom_description
                              : null,
                      }
                    : eventInitialState().data,
        };
    },

    prepareStoreData({
        initData,
        pageType,
        experimentUtils,
        infoPanelCookie = null,
        currentSearchState = null,
        serverRequestUrl = null,
    }) {
        const {
            city: {data: city},
            destination: {data: destination},
            searchRequest,
        } = initData;
        const {
            display_name: cityDisplayName,
            slug: citySlug,
            zoom_level: cityZoom,
            default_map_zoom_level: cityDefaultMapZoom,
        } = city;
        const monthly = searchRequest ? searchRequest.monthly : false;
        const queryParams = serverRequestUrl
            ? Arg.parse(serverRequestUrl)
            : Arg.parse(window.location.search);
        const searchString = searchRequest ? searchRequest.search_string : null;
        const overrideIdealZoom = destination.override_ideal_zoom || false;
        const hasDestination = !isEmpty(destination);
        const serverInfoPanelHidden = !isEmpty(infoPanelCookie)
            ? JSON.parse(infoPanelCookie)
            : false;
        let defaultToMobileMapView = false;

        if (isMobile && pageType === 'city' && citySlug === 'nyc') {
            const nycDefaultMapViewVariation = experimentUtils.getVariation({
                experiment: 'nyc_default_map_view',
            });

            if (nycDefaultMapViewVariation === 'nyc_default_map_view') {
                defaultToMobileMapView = true;
            }
        }

        const searchState = currentSearchState
            ? {
                  isPending: false,
                  data: {
                      ...SearchUtils.defaultSearchSettings,
                      searchMapInstance:
                          currentSearchState.data.searchMapInstance,
                      isMobileListViewActive: defaultToMobileMapView
                          ? false
                          : currentSearchState.data.isMobileListViewActive,
                      isInfoPanelVisible: isBrowser
                          ? !StorageUtils.get(Config.infoPanelCookie, 'cookie')
                          : !serverInfoPanelHidden,
                      sortBy: currentSearchState.data.sortBy,
                  },
                  error: null,
              }
            : {
                  isPending: false,
                  data: {
                      ...SearchUtils.defaultSearchSettings,
                      isMobileListViewActive: !defaultToMobileMapView,
                      isInfoPanelVisible: isBrowser
                          ? !StorageUtils.get(Config.infoPanelCookie, 'cookie')
                          : !serverInfoPanelHidden,
                      sortBy: 'distance',
                  },
                  error: null,
              };
        let searchType = searchState.data.searchType;

        switch (pageType) {
            case 'city':
                searchType = 'city';
                break;

            case 'event':
                searchType = 'event';
                break;

            case 'destination':
                if (destination.has_upcoming_events) {
                    searchType = 'venue';
                } else {
                    searchType = 'destination';
                }

                break;

            default:
                searchType = 'address';
        }

        const breadcrumbs = this.prepareBreadcrumbs(initData, searchType);

        const initialState = {
            ...searchState,
            data: {
                ...searchState.data,
                searchType,
                breadcrumbs,
                overrideIdealZoom:
                    searchType === 'city' && cityZoom
                        ? true
                        : overrideIdealZoom,
                zoom:
                    searchType === 'city'
                        ? SearchUtils.getCityZoom({
                              city,
                              monthly,
                              mobile: isMobile,
                          })
                        : hasDestination
                        ? overrideIdealZoom &&
                          (destination.desktop_zoom_level ||
                              destination.mobile_zoom_level)
                            ? SearchUtils.getDestinationZoom({
                                  destination,
                                  monthly,
                                  mobile: isMobile,
                              })
                            : destination.default_map_zoom_level ||
                              cityDefaultMapZoom
                        : cityDefaultMapZoom,
                isDefaultTimes: isNil(queryParams.starts),
                referrer: queryParams.referrer,
                referrerAffiliate: queryParams.sha_affiliate,
                destinationTitle:
                    searchType === 'city'
                        ? cityDisplayName
                        : destination.title
                        ? destination.title
                        : !isEmpty(searchString)
                        ? searchString
                        : 'Destination',
                kmlMapLayerEnabled: hasDestination
                    ? destination.kml_map_layer_enabled
                    : false,
                kmlMapLayerURL: hasDestination
                    ? destination.kml_map_layer_url
                    : null,
                mobilePaginationLimit:
                    Config.mobilePaginationLimit ||
                    SearchUtils.defaultSearchSettings.mobilePaginationLimit,
            },
        };

        if (searchRequest && searchRequest.isSearchPage && isBrowser) {
            StorageUtils.set(
                'previous-search',
                {
                    url: `${window.location.pathname}${window.location.search}`,
                    title: initialState.data.destinationTitle,
                },
                'session'
            );
        }

        return initialState;
    },

    prepareRequest({requestType, bounds, searchState, searchRequestState}) {
        const {
            isLowInventoryEnabled,
            isSoldOutEnabled,
        } = this.getSocialProofExperimentSettings();
        const allIncludes = isLowInventoryEnabled
            ? `${Config.facilityIncludes},monthly_inventory_availability`
            : Config.facilityIncludes;
        const newSearchState = {
            ...searchState,
            requestIncrements: 5,
            params: {
                ...searchRequestState,
                include: allIncludes,
                sort: 'distance',
                source: isMobile ? 'web_mobile' : 'web',
                mobile_only: false, // eslint-disable-line camelcase
                show_sold_out: isSoldOutEnabled, // eslint-disable-line camelcase
            },
        };

        if (requestType === 'tiled') {
            const {
                tiles,
                numRequests,
                innerCoords,
            } = SearchTileUtils.createTiles(bounds, newSearchState);

            newSearchState.tiles = tiles;
            newSearchState.requestIncrements = numRequests;

            if (innerCoords) {
                newSearchState.params.inner_coords = innerCoords; // eslint-disable-line camelcase
            }
        } else if (requestType === 'radial') {
            const center = bounds.getCenter();
            const maxDistance =
                gmaps.geometry.spherical.computeDistanceBetween(
                    center,
                    bounds.getNorthEast()
                ) * 0.9;
            const distanceDelta = (
                maxDistance / newSearchState.requestIncrements
            ).toFixed(2);
            const distances = [];
            let distanceIterator = 0;

            times(newSearchState.requestIncrements + 1, () => {
                distances.push(distanceDelta * distanceIterator);
                distanceIterator++;
            });

            newSearchState.params.latitude = center.lat();
            newSearchState.params.longitude = center.lng();
            newSearchState.radialDistances = distances;
        }

        return newSearchState;
    },

    updateRoute({currentUrl, searchParams, adParams}) {
        const {
            monthly,
            latitude,
            longitude,
            starts,
            ends,
            search_string: searchString,
            eid,
            operator_id: operatorId,
            rebook_reservation_id: rebookReservationId,
            airport,
            monthlyEnabled,
            page_info: pageInfo,
            search_url: searchUrl,
        } = searchParams;
        const hash = window.location.hash ? window.location.hash : '';
        const destination = pageInfo ? pageInfo.setup.destination : null;
        const urlParams = {...adParams};
        let cleanedUrl = includes(currentUrl, '/spot-details/')
            ? searchUrl.split(/[?#]/)[0]
            : currentUrl.split(/[?#]/)[0];

        if (!includes(cleanedUrl, 'parking-near-me')) {
            if (includes(cleanedUrl, '-parking')) {
                if (
                    (isNil(destination) && isNil(monthlyEnabled)) ||
                    monthlyEnabled ||
                    (destination && destination.monthly_enabled)
                ) {
                    if (monthly) {
                        if (!includes(cleanedUrl, '-monthly-parking')) {
                            cleanedUrl = cleanedUrl.replace(
                                '-parking',
                                '-monthly-parking'
                            );
                        }
                    } else {
                        cleanedUrl = cleanedUrl.replace(
                            '-monthly-parking',
                            '-parking'
                        );
                    }
                }
            } else {
                urlParams.latitude = latitude;
                urlParams.longitude = longitude;

                if (searchString) {
                    urlParams.search_string = searchString; // eslint-disable-line camelcase
                }

                if (monthly) {
                    urlParams.monthly = monthly;
                }
            }
        }

        if (
            ((destination && destination.is_airport) || airport) &&
            starts &&
            ends &&
            !monthly
        ) {
            urlParams.starts = starts;
            urlParams.ends = ends;
            urlParams.airport = airport;
        }

        if (monthly) {
            delete urlParams.airport;
        }

        if (eid) {
            if (cleanedUrl.endsWith('-parking')) {
                urlParams.eid = eid;
            }
        }

        if (rebookReservationId) {
            urlParams.rebook_reservation_id = rebookReservationId; // eslint-disable-line camelcase
        }

        if (operatorId) {
            urlParams.operator_id = operatorId; // eslint-disable-line camelcase
        }

        return `${cleanedUrl}${
            size(urlParams)
                ? `?${Arg.stringify(urlParams).replace(/%3A%3A/g, '::')}${hash}`
                : ''
        }`;
    },

    hasInfoPanel({searchType, city, destination, event}) {
        return searchType === 'city'
            ? Boolean(city && city.description)
            : Boolean(destination && destination.description) ||
                  Boolean(event && event.description);
    },

    getCityZoom({city, monthly = false, mobile = false}) {
        if (mobile && monthly && city.monthly_mobile_zoom_level) {
            return city.monthly_mobile_zoom_level;
        } else if (mobile && city.mobile_zoom_level) {
            return city.mobile_zoom_level;
        } else if (monthly && city.monthly_zoom_level) {
            return city.monthly_zoom_level;
        } else {
            return city.zoom_level;
        }
    },

    getDestinationZoom({destination, monthly = false, mobile = false}) {
        if (mobile && monthly && destination.monthly_mobile_zoom_level) {
            return destination.monthly_mobile_zoom_level;
        } else if (mobile && destination.mobile_zoom_level) {
            return destination.mobile_zoom_level;
        } else if (monthly && destination.monthly_desktop_zoom_level) {
            return destination.monthly_desktop_zoom_level;
        } else {
            return destination.desktop_zoom_level;
        }
    },

    getSocialProofExperimentSettings() {
        const socialProofFeature = 'transient_social_proof';
        const isSocialProofEnabled = ExperimentUtils.isFeatureEnabled(
            socialProofFeature
        );
        const isLowInventoryEnabled =
            isSocialProofEnabled &&
            ExperimentUtils.getFeatureVariable({
                featureName: socialProofFeature,
                variableName: 'low_inventory',
                type: 'Boolean',
            });
        const isSoldOutEnabled =
            isSocialProofEnabled &&
            ExperimentUtils.getFeatureVariable({
                featureName: socialProofFeature,
                variableName: 'sold_out',
                type: 'Boolean',
            });

        return {
            isLowInventoryEnabled,
            isSoldOutEnabled,
        };
    },

    getReferrerDetails({referrer, previousRental = null}) {
        let fromScreen;

        switch (referrer) {
            case 'favorites':
                fromScreen = 'favorite';
                break;

            case 'list':
                fromScreen = 'list';
                break;

            case 'pin':
                fromScreen = 'map';
                break;

            case 'modal':
                fromScreen = 'product details';
                break;

            case 'widget':
                fromScreen = 'widget';
                break;

            default:
                fromScreen = 'checkout';
                break;
        }

        if (previousRental) {
            fromScreen = 'buy this spot again';
        }

        return fromScreen;
    },
};

export default SearchUtils;
